import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

export default function DetailProjectModal({ onClose, project, clients, onEdit }) {
    const navigate = useNavigate();
    const cliente = clients?.find(c => c.id === project.id_cliente);
    const [arquivos, setArquivos] = useState([]);
    const [fileToDelete, setFileToDelete] = useState(null);
    const fileInputRef = React.useRef(null);

    const getInitials = (name) => {
        if (!name) return '??';
        return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
    };

    const formatStatus = (status) => {
        switch (status?.toLowerCase()) {
            case 'novo': return { color: 'bg-blue-500', text: 'Novo' };
            case 'em andamento': return { color: 'bg-green-500', text: 'Em andamento' };
            case 'concluído': return { color: 'bg-emerald-500', text: 'Concluído' };
            case 'aguardando': return { color: 'bg-orange-500', text: 'Aguardando' };
            default: return { color: 'bg-gray-500', text: capitalize(status) };
        }
    };

    const formatarParaBRL = (valor) => {
        return new Intl.NumberFormat("pt-BR", {
            style: "currency",
            currency: "BRL",
        }).format(valor);
    };

    const formatarData = (data) => {
        return new Date(data).toLocaleDateString("pt-BR");
    };

    const capitalize = (str) => {
        if (!str) return '';
        return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    }

    useEffect(() => {
        fetchArquivos();
    }, []);

    const fetchArquivos = async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL || "https://api.clelton-churrasqueiras.site"}/pdfs/list/${project.id}`);

            if (res.data.arquivos && Array.isArray(res.data.arquivos)) {
                setArquivos(res.data.arquivos);
            } else {
                setArquivos([]); // Não há arquivos
            }
        } catch (err) {
            console.error("Erro ao buscar arquivos:", err);
            setArquivos([]);
        }
    };


    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append("file", file); // O backend espera "pdf"

        try {
            await axios.post(`${process.env.REACT_APP_API_URL || "https://api.clelton-churrasqueiras.site"}/pdfs/upload/${project.id}`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            fetchArquivos();
            if (fileInputRef.current) fileInputRef.current.value = '';
        } catch (err) {
            console.error("Erro ao fazer upload:", err);
        }
    };

    const handleDownload = async (filename) => {
        window.open(`${process.env.REACT_APP_API_URL || "https://api.clelton-churrasqueiras.site"}/pdfs/download/${project.id}/${filename}`, "_blank");
    };

    const handleDelete = (filename) => {
        setFileToDelete(filename);
    };

    const confirmDelete = async () => {
        if (!fileToDelete) return;
        try {
            await axios.delete(`${process.env.REACT_APP_API_URL || "https://api.clelton-churrasqueiras.site"}/pdfs/delete/${project.id}/${fileToDelete}`);
            fetchArquivos();
            setFileToDelete(null);
        } catch (err) {
            console.error("Erro ao deletar:", err);
        }
    };

    return (
        <div className="fixed inset-0 flex justify-center items-center bg-black/70 backdrop-blur-md z-50 p-4">
            <div className="glass-panel p-0 w-full max-w-5xl bg-[#0f172a]/95 shadow-2xl border border-white/10 relative overflow-hidden flex flex-col max-h-[90vh]">

                {/* Header Superior (Sticky) */}
                <div className="flex justify-between items-start p-6 border-b border-white/5 bg-white/[0.02]">
                    <div>
                        <h2 className="text-2xl font-bold text-white tracking-wide">
                            Projeto - {cliente ? cliente.nome : "Cliente não identificado"}
                        </h2>
                        <p className="text-gray-400 mt-1 text-sm font-medium">ID: {project.id}</p>
                    </div>

                    <div className="flex items-center space-x-3">
                        {onEdit && (
                            <button
                                onClick={onEdit}
                                className="glass-button bg-white/5 hover:bg-white/10 text-gray-200 px-4 py-2 flex items-center text-sm font-medium transition-colors"
                            >
                                <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                </svg>
                                Editar
                            </button>
                        )}
                        <button onClick={onClose} className="p-2 text-gray-400 hover:text-white transition-colors ml-2">
                            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Conteúdo Rolável */}
                <div className="p-8 overflow-y-auto custom-scrollbar flex-1">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

                        {/* Coluna Esquerda: Visão Geral */}
                        <div className="space-y-6">
                            <h3 className="text-lg font-semibold text-gray-200 mb-4 flex items-center">
                                Visão Geral
                            </h3>

                            {/* Card Cronograma */}
                            <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                                <div className="flex items-center text-gray-300 mb-4">
                                    <svg className="w-5 h-5 mr-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                    <span className="font-medium text-sm">Cronograma</span>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <p className="text-xs text-gray-500 mb-1">Data de Início</p>
                                        <p className="text-sm font-semibold text-gray-200">{formatarData(project.data_inicio)}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500 mb-1">Data de Término</p>
                                        <p className="text-sm font-semibold text-gray-200">{formatarData(project.data_termino)}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Card Financeiro */}
                            <div className="bg-[#102422] border border-green-500/20 rounded-xl p-5 shadow-[0_0_15px_rgba(34,197,94,0.05)] relative overflow-hidden">
                                <div className="absolute top-0 right-0 w-32 h-32 bg-green-500/5 rounded-full -mr-10 -mt-10 blur-2xl"></div>
                                <div className="flex items-center text-green-400 mb-4 relative z-10">
                                    <svg className="w-5 h-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <span className="font-medium text-sm">Financeiro</span>
                                </div>
                                <div className="relative z-10">
                                    <p className="text-xs text-green-500/70 mb-1">Valor</p>
                                    <p className="text-2xl font-bold text-green-400">{formatarParaBRL(project.valor)}</p>
                                </div>
                            </div>

                            {/* Card Status */}
                            <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                                <div className="flex items-center text-gray-300 mb-4">
                                    <svg className="w-5 h-5 mr-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <span className="font-medium text-sm">Status</span>
                                </div>
                                <div className="flex items-center">
                                    <span className={`w-2.5 h-2.5 rounded-full ${formatStatus(project.status).color} mr-2 shadow-[0_0_8px_currentColor] opacity-80`}></span>
                                    <span className="text-sm font-semibold text-gray-200">{formatStatus(project.status).text}</span>
                                </div>
                            </div>

                        </div>

                        {/* Coluna Direita: Cliente & Documentos */}
                        <div className="space-y-6">
                            <h3 className="text-lg font-semibold text-gray-200 mb-4 flex items-center">
                                Cliente do Projeto
                            </h3>

                            <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                                <div className="flex items-center mb-6">
                                    <div className="w-12 h-12 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center font-bold text-lg border border-blue-500/30">
                                        {getInitials(cliente?.nome)}
                                    </div>
                                    <div className="ml-4">
                                        <h4 
                                            className="text-base font-bold text-blue-400 flex items-center cursor-pointer hover:underline"
                                            onClick={() => {
                                                if (cliente?.id) {
                                                    onClose();
                                                    navigate('/clients', { state: { openClientId: cliente.id } });
                                                }
                                            }}
                                        >
                                            {cliente ? cliente.nome : "Cliente não identificado"}
                                            <svg className="w-4 h-4 ml-1 opacity-70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                            </svg>
                                        </h4>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <div className="grid grid-cols-[100px_1fr] items-center">
                                        <span className="text-xs text-gray-500">CPF/CNPJ</span>
                                        <span className="text-sm text-gray-200 font-medium">{cliente?.cpf_cnpj || "-"}</span>
                                    </div>
                                    <div className="grid grid-cols-[100px_1fr] items-center">
                                        <span className="text-xs text-gray-500">Email</span>
                                        {cliente?.email ? (
                                            <span className="text-sm text-gray-200 font-medium">{cliente.email}</span>
                                        ) : (
                                            <span className="text-sm text-gray-200 font-medium">-</span>
                                        )}
                                    </div>
                                    <div className="grid grid-cols-[100px_1fr] items-center">
                                        <span className="text-xs text-gray-500">Telefone</span>
                                        {cliente?.telefone ? (
                                            <span className="text-sm text-gray-200 font-medium">{cliente.telefone}</span>
                                        ) : (
                                            <span className="text-sm text-gray-200 font-medium">-</span>
                                        )}
                                    </div>
                                    <div className="grid grid-cols-[100px_1fr] items-start">
                                        <span className="text-xs text-gray-500 mt-0.5">Logradouro</span>
                                        <span className="text-sm text-gray-200 font-medium leading-tight">
                                            {cliente?.rua ? `${cliente.rua}${cliente.bairro ? `, ${cliente.bairro}` : ''}${cliente.cidade ? ` - ${cliente.cidade}` : ''}` : "-"}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Seção Documentos */}
                            <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden flex flex-col">
                                <div className="p-4 border-b border-white/5 flex justify-between items-center bg-white/[0.02]">
                                    <div className="flex items-center text-gray-200">
                                        <svg className="w-5 h-5 mr-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                        </svg>
                                        <h4 className="font-semibold text-sm">Documentos</h4>
                                    </div>

                                    <label className="cursor-pointer glass-button bg-white/5 hover:bg-white/10 text-gray-300 text-xs font-medium py-1.5 px-3 rounded-lg transition-colors border border-white/5 flex items-center">
                                        Upload de Arquivos
                                        <input
                                            type="file"
                                            accept="application/pdf"
                                            onChange={handleFileUpload}
                                            ref={fileInputRef}
                                            className="hidden"
                                        />
                                    </label>
                                </div>

                                <div className="p-4 max-h-48 overflow-y-auto custom-scrollbar">
                                    <div className="grid grid-cols-[100px_1fr] items-center mb-3">
                                        <span className="text-[10px] uppercase text-gray-500 font-semibold tracking-wider col-span-2">Título</span>
                                    </div>

                                    {arquivos.length > 0 ? (
                                        <ul className="space-y-2">
                                            {arquivos.map((filename) => {
                                                const isPdf = filename.toLowerCase().endsWith('.pdf');
                                                const isDoc = filename.toLowerCase().endsWith('.doc') || filename.toLowerCase().endsWith('.docx');

                                                return (
                                                    <li key={filename} className="flex justify-between items-center py-2 border-b border-white/5 last:border-0 group">
                                                        <div className="flex items-center overflow-hidden">
                                                            <div className={`w-8 h-8 rounded flex items-center justify-center mr-3 flex-shrink-0 ${isPdf ? 'bg-red-500/20 text-red-400' : isDoc ? 'bg-blue-500/20 text-blue-400' : 'bg-gray-500/20 text-gray-400'}`}>
                                                                {isPdf ? (
                                                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm-1.25 17h-1.5v-3.5h-1.5v3.5h-1.5v-8h3.5c1.93 0 3.5 1.57 3.5 3.5 0 1.58-1.04 2.92-2.5 3.36v1.14zm4.75 0h-1.5v-8h1.5v8zm3.5-3.5h-2v3.5h-1.5v-8h4v1.5h-2.5v1.75h2v1.5h-2v1.75h2.5v1.5zm-8-3c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5h1.5v-3h-1.5z" /></svg>
                                                                ) : isDoc ? (
                                                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                                                                ) : (
                                                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                                                                )}
                                                            </div>
                                                            <span className="text-sm text-gray-300 font-medium truncate" title={filename}>{filename}</span>
                                                        </div>
                                                        <div className="flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                            <button
                                                                onClick={() => handleDownload(filename)}
                                                                className="text-gray-400 hover:text-white p-1 rounded hover:bg-white/10 transition-colors"
                                                                title="Baixar"
                                                            >
                                                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                                                            </button>
                                                            <button
                                                                onClick={() => handleDelete(filename)}
                                                                className="text-gray-400 hover:text-red-400 p-1 rounded hover:bg-red-500/10 transition-colors"
                                                                title="Excluir"
                                                            >
                                                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                                            </button>
                                                        </div>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    ) : (
                                        <div className="text-center py-6 text-sm text-gray-500">
                                            Nenhum documento encontrado.
                                        </div>
                                    )}
                                </div>
                            </div>

                        </div>
                    </div>
                </div>

                {/* Footer Fixo */}
                <div className="p-4 border-t border-white/5 bg-white/[0.02]">
                    <button
                        onClick={onClose}
                        className="glass-button bg-white/5 hover:bg-white/10 text-gray-300 w-full py-2.5 font-medium transition-colors border border-white/5"
                    >
                        Fechar
                    </button>
                </div>
            </div>

            {/* Delete File Confirmation Modal */}
            {fileToDelete && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex justify-center items-center z-[60]">
                    <div className="glass-panel p-6 w-full max-w-md bg-slate-900 shadow-2xl border-white/10">
                        <h3 className="text-xl font-bold text-white mb-4">Excluir Arquivo</h3>
                        <p className="text-gray-300 mb-6">
                            Tem certeza que deseja excluir o arquivo <span className="font-semibold text-white">{fileToDelete}</span>? Esta ação não poderá ser desfeita.
                        </p>
                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => setFileToDelete(null)}
                                className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-md transition-colors font-medium text-sm border border-white/10"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={confirmDelete}
                                className="px-4 py-2 bg-red-500/80 hover:bg-red-500 text-white rounded-md transition-colors font-medium text-sm shadow-[0_0_10px_rgba(239,68,68,0.3)]"
                            >
                                Sim, excluir
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
